import { useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inactivity timeout: 30 minutes (in milliseconds)
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
// Warning shown this many ms before auto-logout
const WARNING_BEFORE_MS = 5 * 60 * 1000;
// How often to refresh the server session when the user is active (every 10 min)
const SESSION_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

/**
 * useAutoLogout — Tracks user inactivity and auto-logs out after 30 min.
 *
 * Behaviour:
 *  - Resets a timer on every user interaction (mouse, keyboard, scroll, touch)
 *  - At 25 min inactivity → shows a warning toast ("Session expiring in 5 minutes")
 *  - At 30 min inactivity → calls logout API, shows toast, redirects to /login
 *  - While active → refreshes the server session every 10 min (sliding window)
 *  - On 401 from any axios call → same logout + toast flow
 *
 * @param {Function} onLogout  Called to perform the actual logout (navigate + clear state)
 * @param {boolean}  enabled   Set to false when user is not authenticated
 */
const useAutoLogout = (onLogout, enabled = true) => {
  const lastActivityRef = useRef(Date.now());
  const warningToastIdRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const sessionRefreshTimerRef = useRef(null);
  const isLoggingOutRef = useRef(false);

  const performLogout = useCallback(
    async (reason = "inactivity") => {
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;

      // Stop all timers immediately so nothing fires after logout
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (sessionRefreshTimerRef.current) clearInterval(sessionRefreshTimerRef.current);

      // Dismiss any lingering warning toast
      if (warningToastIdRef.current) {
        toast.dismiss(warningToastIdRef.current);
        warningToastIdRef.current = null;
      }

      try {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      } catch {
        // Ignore errors — cookie will be cleared regardless
      }

      const message =
        reason === "token_expired"
          ? "Your session has expired. Please log in again."
          : "You have been logged out due to inactivity.";

      toast.warning(message, {
        duration: 6000,
        description: reason === "token_expired" ? undefined : "No activity was detected for 30 minutes.",
      });

      onLogout();
    },
    [onLogout]
  );

  // Refresh the server-side session (extends cookie + DB expiry by 30 min)
  const refreshServerSession = useCallback(async () => {
    // Don't attempt a refresh if we're already on an auth page — avoids
    // triggering the global 401 interceptor from within the hook itself.
    if (window.location.pathname === "/login" || window.location.pathname === "/register") return;
    try {
      await axios.post(`${API}/auth/refresh-session`, {}, { withCredentials: true });
    } catch (error) {
      // 401 means session already expired. The global interceptor excludes
      // /auth/refresh-session so this won't trigger a redundant redirect.
      console.log("Session refresh skipped (session expired):", error?.response?.status);
    }
  }, []);

  const resetTimers = useCallback(() => {
    if (!enabled) return;
    lastActivityRef.current = Date.now();

    // Clear existing timers
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

    // Dismiss any active warning
    if (warningToastIdRef.current) {
      toast.dismiss(warningToastIdRef.current);
      warningToastIdRef.current = null;
    }

    // Schedule warning at 25-min mark
    warningTimerRef.current = setTimeout(() => {
      warningToastIdRef.current = toast.warning(
        "Session expiring soon",
        {
          description: "You will be logged out in 5 minutes due to inactivity. Move your mouse or press a key to stay logged in.",
          duration: WARNING_BEFORE_MS, // stays until dismissed or auto-logout
          action: {
            label: "Stay logged in",
            onClick: () => {
              resetTimers();
              refreshServerSession();
            },
          },
        }
      );
    }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Schedule hard logout at 30-min mark
    logoutTimerRef.current = setTimeout(() => {
      performLogout("inactivity");
    }, INACTIVITY_TIMEOUT_MS);
  }, [enabled, performLogout, refreshServerSession]);

  useEffect(() => {
    if (!enabled) return;

    // Activity events that reset the inactivity timer
    const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keypress", "keydown", "scroll", "touchstart", "click"];

    const handleActivity = () => resetTimers();

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );

    // Start timers on mount
    resetTimers();

    // Refresh server session every 10 min while user is active
    sessionRefreshTimerRef.current = setInterval(() => {
      const idleSecs = (Date.now() - lastActivityRef.current) / 1000;
      // Only refresh if user was active in the last 10 min
      if (idleSecs < SESSION_REFRESH_INTERVAL_MS / 1000) {
        refreshServerSession();
      }
    }, SESSION_REFRESH_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleActivity)
      );
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (sessionRefreshTimerRef.current) clearInterval(sessionRefreshTimerRef.current);
    };
  }, [enabled, resetTimers, refreshServerSession]);

  return { performLogout };
};

export default useAutoLogout;
